// Calculator registry. A calculator is a plain definition object with a
// `calculate(inputs, ctx)` function. Full definitions are registered
// lazily (only when a user visits that calculator's page). Lightweight
// metadata is always available for listing and navigation.

import { CALCULATOR_LINEUP } from "./lineup";
import { CALCULATOR_METADATA } from "./metadata";

const _registry = new Map();

export function registerCalculator(definition) {
  if (!definition || !definition.id) {
    throw new Error("Calculator definition requires an id.");
  }
  if (_registry.has(definition.id)) {
    throw new Error(`Calculator already registered: ${definition.id}`);
  }
  _registry.set(definition.id, Object.freeze({ ...definition }));
  return definition.id;
}

/** Get a full calculator definition by id or slug (null if not yet loaded). */
export function getCalculator(idOrSlug) {
  if (_registry.has(idOrSlug)) return _registry.get(idOrSlug);
  for (const def of _registry.values()) {
    if (def.slug === idOrSlug) return def;
  }
  return null;
}

/** Get lightweight metadata by id or slug (always available, no dynamic import). */
export function getMetadata(idOrSlug) {
  for (const m of CALCULATOR_METADATA) {
    if (m.id === idOrSlug || m.slug === idOrSlug) return m;
  }
  return null;
}

/** List all calculators as lightweight metadata, ordered by the lineup. */
export function listCalculators() {
  const order = new Map(CALCULATOR_LINEUP.map((entry, i) => [entry.slug, i]));
  return [...CALCULATOR_METADATA].sort((a, b) => {
    const aIdx = order.has(a.slug) ? order.get(a.slug) : Number.MAX_SAFE_INTEGER;
    const bIdx = order.has(b.slug) ? order.get(b.slug) : Number.MAX_SAFE_INTEGER;
    return aIdx - bIdx;
  });
}

export function isAvailableIn(definition, countryCode) {
  if (!definition.countries) return true;
  return definition.countries.includes("*") || definition.countries.includes(countryCode);
}